import { message } from 'antd'
import type { Middleware } from 'openapi-fetch'

import { authService } from '@/services/authService'

const UNPROTECTED_ROUTES = [
  '/auth/refresh',
  '/auth/oauth',
  '/auth/passwordless',
]

const isUnprotectedRoute = (path: string) =>
  UNPROTECTED_ROUTES.some((p) => path.startsWith(p))

async function handleAuthFailure(error: unknown): Promise<void> {
  console.error('Auth failed:', error)
  authService.logout()
  void message.error('Session expired. Please sign in again.')
  window.location.href = '/login'
}

const requestClones = new WeakMap<Request, Request>()
const retriedRequests = new WeakSet<Request>()

export const authMiddleware: Middleware = {
  async onRequest({ schemaPath, request }) {
    request.headers.set('Content-Type', 'application/json')
    if (isUnprotectedRoute(schemaPath)) return undefined

    try {
      const { accessToken } = await authService.getTokens()
      request.headers.set('Authorization', `Bearer ${accessToken.value}`)
      requestClones.set(request, request.clone())
      return request
    } catch (error) {
      await handleAuthFailure(error)
      throw error
    }
  },

  async onResponse({ response, request, schemaPath }) {
    if (isUnprotectedRoute(schemaPath)) return response

    if (response.status === 403) {
      void message.error(
        "Access denied. You don't have permission for this action.",
      )
      return response
    }

    if (response.status !== 401 || retriedRequests.has(request)) {
      return response
    }

    retriedRequests.add(request)
    const clone = requestClones.get(request)
    if (!clone) return response

    try {
      const { accessToken } = await authService.getTokens({
        forceRefresh: true,
      })
      clone.headers.set('Authorization', `Bearer ${accessToken.value}`)
      const retry = await fetch(clone)
      if (retry.status === 401) {
        await handleAuthFailure(new Error('Retry returned 401'))
      }
      return retry
    } catch (error) {
      await handleAuthFailure(error)
      return response
    }
  },
}
